/**
 * WidgetCreatorWizard — kreator definicji widgetu danych (Faza 23, T1.7).
 *
 * Kreator 6-krokowy (Typ → Źródło → Metryki → Filtry → Wygląd → Podgląd) osadzony w Modalu.
 * Trzyma JEDEN stan szkicu (`WidgetWizardDraft`) na poziomie kreatora — kroki są kontrolowane,
 * nic nie zapisuje się do backendu przed „Zakończ" (wzorzec z `WidgetConfigModal.tsx`: `draft` +
 * `patch()`). Nawigacja sekwencyjna bez klikania w nagłówek kroku (wzorzec `WizardElement.tsx`),
 * ale — inaczej niż tam — „Dalej" jest zablokowane dopóki bieżący krok nie jest kompletny
 * (`isStepValid`), żeby nie dało się dojść do „Zakończ" z niespójnym configiem.
 *
 * MVP kreatora NIE wystawia `compatibleEntities` — to pole zastane, dziś nieużywane (host formularza
 * dostarczony w Etapie 3 wiąże widget „z obecności rekordu", a picker filtruje po `contextEntities`
 * źródła, nie po tym polu — WIDGETS.md §5.3/§7). Kreator nie wysyła go w ogóle, więc PATCH nie kasuje
 * istniejącej wartości przy edycji. (Dawne pole `config.bindToRecord` USUNIĘTE w Etapie 3.)
 *
 * `config` end-to-end trzyma tylko pola właściwe dla wybranego typu wizualnego — `buildFinalConfig`
 * składa OSTATECZNY kształt tuż przed podglądem/zapisem (obrona przed niespójnym stanem po
 * kilkukrotnej zmianie typu/pola w trakcie sesji kreatora).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import {
  type CreateWidgetDefinitionInput,
  useWidgetDefinitionMutations,
  useWidgetSources,
} from '@/hooks/useWidgetDefinitions';
import type {
  WidgetDefinitionConfig,
  WidgetDefinitionDTO,
  WidgetFilterSpec,
  WidgetSourceMeta,
  WidgetVisualType,
} from '@/lib/widgets/types';

import { WizardStepAppearance } from './WizardStepAppearance';
import { WizardStepFilters } from './WizardStepFilters';
import { WizardStepMetrics } from './WizardStepMetrics';
import { WizardStepPreview } from './WizardStepPreview';
import { WizardStepSource } from './WizardStepSource';
import { WizardStepType } from './WizardStepType';

const STEP_IDS = ['type', 'source', 'metrics', 'filters', 'appearance', 'preview'] as const;
export type WidgetWizardStepId = (typeof STEP_IDS)[number];

const DEFAULT_TABLE_LIMIT = 20;
const DEFAULT_ICON = 'BarChart3';

export interface WidgetWizardDraft {
  visualType: WidgetVisualType | null;
  source: string | null;
  config: WidgetDefinitionConfig;
  name: string;
  description: string;
  icon: string;
  isActive: boolean;
}

function buildInitialDraft(editing: WidgetDefinitionDTO | null): WidgetWizardDraft {
  if (editing) {
    return {
      visualType: editing.visualType,
      source: editing.source,
      config: editing.config,
      name: editing.name,
      description: editing.description ?? '',
      icon: editing.icon ?? DEFAULT_ICON,
      isActive: editing.isActive,
    };
  }
  return {
    visualType: null,
    source: null,
    config: {},
    name: '',
    description: '',
    icon: DEFAULT_ICON,
    isActive: true,
  };
}

/** Kind zadeklarowanego pola źródła (undefined gdy pole nieznane/puste) — reużywane przez kroki. */
export function fieldKind(source: WidgetSourceMeta | undefined, field: string | undefined) {
  if (!field) return undefined;
  return source?.fields.find((f) => f.slug === field)?.kind;
}

/** Ludzka etykieta pola źródła (fallback: slug) — reużywane przez kroki. */
export function fieldLabel(
  source: WidgetSourceMeta | undefined,
  field: string | undefined,
): string {
  if (!field) return '';
  return source?.fields.find((f) => f.slug === field)?.label ?? field;
}

/** Czy pole jest datą/znacznikiem czasu — decyduje o obowiązkowym kubełku przy grupowaniu. */
export function isDateKindField(
  source: WidgetSourceMeta | undefined,
  field: string | undefined,
): boolean {
  const kind = fieldKind(source, field);
  return kind === 'date' || kind === 'timestamp';
}

/** Szkielet configu odpowiedni dla nowo wybranego typu wizualnego — patrz docstring modułu. */
export function baseConfigForType(
  visualType: WidgetVisualType,
  source: WidgetSourceMeta | undefined,
  filters: WidgetFilterSpec[] | undefined,
): WidgetDefinitionConfig {
  const filtersPart: Pick<WidgetDefinitionConfig, 'filters'> =
    filters && filters.length > 0 ? { filters } : {};
  if (visualType === 'table') {
    return { table: { columns: [], limit: DEFAULT_TABLE_LIMIT }, ...filtersPart };
  }
  if (visualType === 'kpi') {
    return { aggregate: { fn: 'count' }, ...filtersPart };
  }
  const defaultField = source?.defaultDateField ?? '';
  const needsBucket = defaultField ? isDateKindField(source, defaultField) : false;
  return {
    aggregate: { fn: 'count' },
    groupBy: { field: defaultField, ...(needsBucket ? { dateBucket: 'month' as const } : {}) },
    ...filtersPart,
  };
}

function isMetricsStepValid(
  draft: WidgetWizardDraft,
  source: WidgetSourceMeta | undefined,
): boolean {
  if (!source || !draft.visualType) return false;
  if (draft.visualType === 'table') {
    const table = draft.config.table;
    return !!table && table.columns.length > 0 && Number.isFinite(table.limit) && table.limit > 0;
  }
  const agg = draft.config.aggregate;
  if (!agg) return false;
  if (agg.fn === 'sum' || agg.fn === 'avg' || agg.fn === 'min' || agg.fn === 'max') {
    if (!agg.field || fieldKind(source, agg.field) !== 'numeric') return false;
  } else if (agg.fn === 'count_distinct' && !agg.field) {
    return false;
  }
  if (draft.visualType === 'kpi') return true;
  const gb = draft.config.groupBy;
  if (!gb || !gb.field) return false;
  if (isDateKindField(source, gb.field) && !gb.dateBucket) return false;
  return true;
}

function isStepValid(
  stepId: WidgetWizardStepId,
  draft: WidgetWizardDraft,
  source: WidgetSourceMeta | undefined,
): boolean {
  switch (stepId) {
    case 'type':
      return draft.visualType !== null;
    case 'source':
      return draft.source !== null;
    case 'metrics':
      return isMetricsStepValid(draft, source);
    case 'filters':
      return true;
    case 'appearance':
      return draft.name.trim().length > 0;
    case 'preview':
      return true;
    default:
      return false;
  }
}

/** Ostateczny `config` — dokłada tylko pola właściwe dla typu wizualnego (§ config assembly rules). */
function buildFinalConfig(
  draft: WidgetWizardDraft,
  source: WidgetSourceMeta | undefined,
): WidgetDefinitionConfig {
  const cleanFilters = draft.config.filters?.filter(
    (f) => f.field && String(f.value ?? '').trim() !== '',
  );
  const filtersPart: Pick<WidgetDefinitionConfig, 'filters'> =
    cleanFilters && cleanFilters.length > 0 ? { filters: cleanFilters } : {};

  if (draft.visualType === 'table' && draft.config.table) {
    return { table: draft.config.table, ...filtersPart };
  }
  const agg = draft.config.aggregate;
  if (!agg) return draft.config;
  const finalAgg = agg.fn === 'count' ? { fn: agg.fn } : { fn: agg.fn, field: agg.field };
  if (draft.visualType === 'kpi') {
    return { aggregate: finalAgg, ...filtersPart };
  }
  const gb = draft.config.groupBy;
  if (!gb) return draft.config;
  const dateKind = isDateKindField(source, gb.field);
  return {
    aggregate: finalAgg,
    groupBy: {
      field: gb.field,
      ...(dateKind && gb.dateBucket ? { dateBucket: gb.dateBucket } : {}),
      ...(gb.maxItems ? { maxItems: gb.maxItems } : {}),
    },
    ...filtersPart,
  };
}

export interface WidgetCreatorWizardProps {
  open: boolean;
  editing: WidgetDefinitionDTO | null;
  onClose: () => void;
}

export function WidgetCreatorWizard({ open, editing, onClose }: WidgetCreatorWizardProps) {
  const { t } = useTranslation();
  const [stepIdx, setStepIdx] = useState(0);
  const [draft, setDraft] = useState<WidgetWizardDraft>(() => buildInitialDraft(editing));
  // Podpis stanu startowego zapamiętany w chwili otwarcia (nie liczony w każdym renderze), żeby
  // odświeżenie listy z nowszą wersją tej samej definicji nie wywołało fałszywego pytania.
  const [baselineSignature, setBaselineSignature] = useState(() =>
    JSON.stringify(buildInitialDraft(editing)),
  );

  // Reset szkicu i kroku przy KAŻDYM otwarciu (nowy widget albo inny wiersz do edycji) — modal
  // jest jednym stałym elementem drzewa (nie unmountuje się między otwarciami).
  useEffect(() => {
    if (open) {
      const initial = buildInitialDraft(editing);
      setDraft(initial);
      setBaselineSignature(JSON.stringify(initial));
      setStepIdx(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  const sourcesQuery = useWidgetSources({ enabled: open });
  const sources = sourcesQuery.data ?? [];
  const selectedSource = sources.find((s) => s.key === draft.source);

  const { createDefinition, updateDefinition } = useWidgetDefinitionMutations();
  const isSaving = createDefinition.isPending || updateDefinition.isPending;

  function patch(p: Partial<WidgetWizardDraft>) {
    setDraft((d) => ({ ...d, ...p }));
  }
  function patchConfig(p: Partial<WidgetDefinitionConfig>) {
    setDraft((d) => ({ ...d, config: { ...d.config, ...p } }));
  }

  function handleVisualTypeChange(next: WidgetVisualType) {
    if (next === draft.visualType) return;
    setDraft((d) => ({
      ...d,
      visualType: next,
      config: baseConfigForType(next, selectedSource, d.config.filters),
    }));
  }

  function handleSourceChange(nextKey: string) {
    if (nextKey === draft.source) return;
    const nextSource = sources.find((s) => s.key === nextKey);
    setDraft((d) => ({
      ...d,
      source: nextKey,
      config: d.visualType ? baseConfigForType(d.visualType, nextSource, undefined) : {},
    }));
  }

  const stepId = STEP_IDS[stepIdx] ?? 'type';
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === STEP_IDS.length - 1;
  const canAdvance = isStepValid(stepId, draft, selectedSource);
  const finalConfig = buildFinalConfig(draft, selectedSource);
  const isDraftDirty = JSON.stringify(draft) !== baselineSignature;

  // Krok jest osiągalny, gdy WSZYSTKIE kroki przed nim są kompletne — pozwala kliknąć powrót do
  // dowolnego ukończonego kroku, a do przodu tylko przez poprawny ciąg (nie przeskoczysz
  // niekompletnego kroku). Zero dodatkowego stanu — wyprowadzone z tej samej walidacji co „Dalej".
  function canNavigateTo(target: number): boolean {
    for (let i = 0; i < target; i++) {
      const sid = STEP_IDS[i];
      if (!sid || !isStepValid(sid, draft, selectedSource)) return false;
    }
    return true;
  }

  function handleFinish() {
    if (!draft.visualType || !draft.source || isSaving) return;
    const input: CreateWidgetDefinitionInput = {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      icon: draft.icon || null,
      visualType: draft.visualType,
      source: draft.source,
      config: finalConfig,
      isActive: draft.isActive,
    };
    if (editing) {
      updateDefinition.mutate({ id: editing.id, values: input }, { onSuccess: onClose });
    } else {
      createDefinition.mutate(input, { onSuccess: onClose });
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        editing ? t('tenant.widgets.wizard.titleEdit') : t('tenant.widgets.wizard.titleCreate')
      }
      size="xl"
      expandable
      fixedHeight
      guardUnsaved
      // Typ i źródło wybiera się kliknięciem (bez zdarzeń wpisywania) — „zmieniono" liczymy
      // porównaniem szkicu ze stanem startowym.
      dirty={isDraftDirty}
      // W trakcie zapisu nie da się zamknąć okna tłem ani Esc (jak „Anuluj").
      closeOnBackdrop={!isSaving}
      closeOnEsc={!isSaving}
      footer={
        <div className="flex w-full items-center justify-between">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isSaving}
            data-testid="widget-wizard-cancel"
          >
            {t('common.cancel')}
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              disabled={isFirst || isSaving}
              data-testid="widget-wizard-back"
            >
              {t('common.back')}
            </Button>
            {isLast ? (
              <Button
                variant="primary"
                onClick={handleFinish}
                loading={isSaving}
                disabled={!canAdvance || isSaving}
                data-testid="widget-wizard-finish"
              >
                {t('tenant.widgets.wizard.nav.finish')}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => setStepIdx((i) => Math.min(STEP_IDS.length - 1, i + 1))}
                disabled={!canAdvance}
                data-testid="widget-wizard-next"
              >
                {t('tenant.widgets.wizard.nav.next')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-4" data-testid="widget-wizard">
        <div className="space-y-2">
          <div className="text-xs text-text-secondary">
            {t('tenant.widgets.wizard.stepOf', { current: stepIdx + 1, total: STEP_IDS.length })}
          </div>
          <nav
            className="flex flex-wrap gap-1.5"
            aria-label={t('tenant.widgets.wizard.stepsNav')}
            data-testid="widget-wizard-steps"
          >
            {STEP_IDS.map((sid, i) => {
              const isCurrent = i === stepIdx;
              // Bieżący krok jest zawsze osiągalny — nie licz prefiksu na darmo (i tak odrzucany
              // przez `disabled` przy `isCurrent`).
              const reachable = isCurrent || canNavigateTo(i);
              return (
                <Button
                  key={sid}
                  variant={isCurrent ? 'primary' : 'ghost'}
                  onClick={() => setStepIdx(i)}
                  disabled={isSaving || (!isCurrent && !reachable)}
                  aria-current={isCurrent ? 'step' : undefined}
                  className="h-auto px-2.5 py-1 text-xs"
                  data-testid={`widget-wizard-step-${sid}`}
                >
                  {i + 1}. {t(`tenant.widgets.wizard.steps.${sid}`)}
                </Button>
              );
            })}
          </nav>
        </div>

        {stepId === 'type' && (
          <WizardStepType visualType={draft.visualType} onChange={handleVisualTypeChange} />
        )}
        {stepId === 'source' && (
          <WizardStepSource
            sources={sources}
            loading={sourcesQuery.isLoading}
            value={draft.source}
            onChange={handleSourceChange}
          />
        )}
        {stepId === 'metrics' && draft.visualType && (
          <WizardStepMetrics
            visualType={draft.visualType}
            config={draft.config}
            source={selectedSource}
            patchConfig={patchConfig}
          />
        )}
        {stepId === 'filters' && (
          <WizardStepFilters
            filters={draft.config.filters}
            source={selectedSource}
            patchConfig={patchConfig}
          />
        )}
        {stepId === 'appearance' && <WizardStepAppearance draft={draft} patch={patch} />}
        {stepId === 'preview' && draft.visualType && draft.source && (
          <WizardStepPreview
            visualType={draft.visualType}
            source={draft.source}
            config={finalConfig}
          />
        )}
      </div>
    </Modal>
  );
}
